import {
  FileCode,
  FolderGit2,
  Monitor,
  NetworkIcon,
  Plug,
  Puzzle,
  Sparkles,
  Users,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { PostureManagementTabs } from './constants';
import type { PostureManagementTabType } from './types';

/**
 * Role and flag inputs the Inventory tabs are gated on. Whether a slot tab
 * (MCP, Extensions, Plugins) has content is the component's concern, not a
 * gate, so the sidebar can evaluate these without rendering anything.
 */
export interface PostureManagementTabGates {
  organizationId?: string;
  canViewAdminContent: boolean;
  hookInventoryAllowed?: boolean;
  projectsInventoryAllowed?: boolean;
  skillsInventoryAllowed?: boolean;
  /** KIR-5412: the Endpoints tab (`shared_devices`). */
  sharedDevicesAllowed?: boolean;
}

export interface PostureManagementTabMeta {
  label: string;
  icon: LucideIcon;
  visible: (gates: PostureManagementTabGates) => boolean;
  /** Rendered as a "New" pill next to the label. */
  isNew?: boolean;
}

/**
 * The Inventory tabs, in display order. PostureManagement renders them as a
 * tab bar and the UI v2 sidebar lists them as items, so a tab added here (and
 * to PostureManagementTabs, which makes this record exhaustive) shows up in
 * both.
 */
export const POSTURE_MANAGEMENT_TAB_META: Record<
  PostureManagementTabType,
  PostureManagementTabMeta
> = {
  // Users first per the design doc; org-wide readers only.
  [PostureManagementTabs.USER_SECURITY_POSTURE]: {
    label: 'Users',
    icon: Users,
    visible: g => !!g.organizationId && g.canViewAdminContent,
  },
  // KIR-5412 — next to Users: one row per physical machine. Org scope only,
  // same read roles as Users (the rows name people and carry IP / MAC).
  [PostureManagementTabs.ENDPOINTS]: {
    label: 'Endpoints',
    icon: Monitor,
    isNew: true,
    visible: g =>
      !!g.organizationId && !!g.sharedDevicesAllowed && g.canViewAdminContent,
  },
  [PostureManagementTabs.PROJECTS]: {
    label: 'Projects',
    icon: FolderGit2,
    visible: g =>
      !!g.organizationId &&
      !!g.projectsInventoryAllowed &&
      g.canViewAdminContent,
  },
  [PostureManagementTabs.MCP]: {
    label: 'MCP Servers',
    icon: NetworkIcon,
    visible: () => true,
  },
  [PostureManagementTabs.EXTENSIONS]: {
    label: 'Extensions',
    icon: Plug,
    visible: () => true,
  },
  [PostureManagementTabs.PLUGINS]: {
    label: 'Plugins',
    icon: Puzzle,
    visible: () => true,
  },
  [PostureManagementTabs.AI_INSTRUCTIONS]: {
    label: 'AI Instructions',
    icon: FileCode,
    visible: g => !!g.organizationId && g.canViewAdminContent,
  },
  // KIR-4699 — right after AI Instructions (the two split what used to be
  // one list). Org scope only: there is no account-scoped skills route.
  [PostureManagementTabs.SKILLS]: {
    label: 'Skills',
    icon: Sparkles,
    isNew: true,
    visible: g =>
      !!g.organizationId && !!g.skillsInventoryAllowed && g.canViewAdminContent,
  },
  // KIR-2917 / KIR-3643 — org scope gates on the read role (owner/manager/
  // support); the personal dashboard has no org role gate. Keep the ternary
  // explicit rather than relying on useRoleCheck returning `true` without an
  // organizationId.
  [PostureManagementTabs.HOOKS]: {
    label: 'Hooks',
    icon: Webhook,
    isNew: true,
    visible: g =>
      !!g.hookInventoryAllowed &&
      (g.organizationId ? g.canViewAdminContent : true),
  },
};
